import React, { useEffect, useState, useMemo } from 'react';
import { Container, Modal, Button } from 'react-bootstrap';
import {
    Plus, Calendar, Clock, MapPin, Tag, FileText, Hand, Trash2,
    Info, CalendarOff, SlidersHorizontal, BadgeCheck,
    CalendarPlus, History, CalendarRange, UserMinus, UserCheck
} from 'lucide-react';
import EventCard from './EventCard';
import ShareMenu from './ShareMenu';
import { Loader, EmptyState, trackPointer, MediaPlaceholder } from './UI';
import { hasCustomImage } from '../utils/Utils';
import { downloadICS, googleCalendarUrl, DEFAULT_DURATION } from '../utils/calendar';

function EventsComponent({ events, categorias, currentUserId, handleDelete, loading }) {
    const [selectedCategory, setSelectedCategory] = useState("Todas");
    const [timeframe, setTimeframe] = useState("proximos"); // "proximos" | "anteriores"
    const [showModal, setShowModal] = useState(false);
    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    // Inscripción pendiente de confirmar: { event, action: "join" | "leave" }
    const [pending, setPending] = useState(null);

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
    };

    // Función para mostrar detalles en el modal
    const handleVerMas = (event) => {
        setSelectedEvent(event);
        setShowModal(true);
    };

    // Función para cerrar el modal
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedEvent(null);
    };

    // ¿Está el usuario ya en la lista de participantes del evento?
    const isApuntado = (event) =>
        (event.participants || []).some(p => String(p?._id || p) === String(currentUserId));

    // Funciones para pedir confirmación antes de apuntarse o desapuntarse
    const handleApuntarme = (event) => setPending({ event, action: 'join' });
    const handleDesapuntarme = (event) => setPending({ event, action: 'leave' });

    // Confirma la acción pendiente: apuntarse (POST) o desapuntarse (DELETE)
    const confirmParticipacion = async () => {
        const { event, action } = pending || {};
        const leaving = action === 'leave';

        try {
            if (!event?._id || !currentUserId) {
                alert('Error: Faltan datos para procesar la inscripción.');
                return;
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/events/${event._id}/participate`, {
                method: leaving ? 'DELETE' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({ userId: currentUserId }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
            }

            alert(leaving
                ? 'Te has desapuntado del evento.'
                : '¡Te has apuntado al evento con éxito!');
            window.location.reload();

        } catch (error) {
            console.error(leaving ? 'Error al desapuntarse:' : 'Error al inscribirse:', error);
            if (error.message.includes('participando')) {
                alert(error.message);
            }
            else if (leaving) {
                alert('Error al desapuntarse del evento. Por favor, inténtalo de nuevo más tarde.');
            }
            else {
                alert('Error al inscribirse al evento. Por favor, inténtalo de nuevo más tarde.');
            }
        } finally {
            setPending(null);
        }
    };

    // Función para cancelar la confirmación
    const cancelParticipacion = () => setPending(null);

    // Tarjeta a la que saltar al abrir un enlace compartido (/events#event-<id>)
    const [anchorId, setAnchorId] = useState(() => window.location.hash.slice(1) || null);

    // El evento compartido puede estar en la otra pestaña o fuera del filtro,
    // así que primero dejamos la vista en la que sí se ve
    useEffect(() => {
        if (loading || !anchorId) return;
        const target = events.find(e => `event-${e._id}` === anchorId);
        if (!target) {
            setAnchorId(null);
            return;
        }
        setTimeframe(isPast(target) ? "anteriores" : "proximos");
        setSelectedCategory("Todas");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, anchorId, events]);

    // Y saltamos a la tarjeta en cuanto está pintada, que es tras ese cambio de vista
    useEffect(() => {
        if (!anchorId) return;
        const card = document.getElementById(anchorId);
        if (!card) return;
        card.scrollIntoView({ behavior: "smooth", block: "center" });
        setAnchorId(null);
    }, [anchorId, loading, events, timeframe, selectedCategory]);

    // Función para formatear la fecha completa
    const formatDate = (dateString) => {
        if (!dateString) return "Sin fecha";
        const options = { year: "numeric", month: "long", day: "numeric" };
        return new Date(dateString).toLocaleDateString("es-ES", options);
    };

    const startOfToday = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const isPast = (event) => new Date(event.date) < startOfToday;

    // Cuántos eventos hay a cada lado, para los contadores de las pestañas
    const counts = useMemo(() => {
        const inCategory = selectedCategory === "Todas"
            ? events
            : events.filter(e => e.category === selectedCategory);
        return {
            proximos: inCategory.filter(e => !isPast(e)).length,
            anteriores: inCategory.filter(e => isPast(e)).length,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events, selectedCategory, startOfToday]);

    // Agrupamos eventos por mes/año
    const groupedEvents = useMemo(() => {
        const past = timeframe === "anteriores";

        let filtered = events.filter(event => (past ? isPast(event) : !isPast(event)));

        if (selectedCategory !== "Todas") {
            filtered = filtered.filter(event => event.category === selectedCategory);
        }

        // Próximos: los más cercanos primero. Anteriores: los más recientes primero.
        filtered.sort((a, b) => past
            ? new Date(b.date) - new Date(a.date)
            : new Date(a.date) - new Date(b.date));

        return filtered.reduce((groups, event) => {
            const monthYear = new Date(event.date).toLocaleDateString("es-ES", {
                month: "long",
                year: "numeric",
            });
            if (!groups[monthYear]) groups[monthYear] = [];
            groups[monthYear].push(event);
            return groups;
        }, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events, selectedCategory, timeframe, startOfToday]);

    // Datos que comparten el .ics y el enlace de Google
    const calendarData = (event) => ({
        uid: event._id,
        title: event.name,
        date: event.date,
        time: event.time,
        minutes: DEFAULT_DURATION.event,
        details: event.description,
        location: event.location,
    });

    const isMyEvent = (event) =>
        event.creator === currentUserId || event.creator?._id === currentUserId;

    // Enlace al evento: la página de eventos anclada en su tarjeta
    const eventUrl = (event) => `${window.location.origin}/events#event-${event._id}`;

    // Fecha, hora y sitio para que el mensaje se entienda sin abrir el enlace
    const shareLines = (event) => [
        formatDate(event.date) + (event.time ? ` · ${event.time}` : ''),
        event.location || null,
    ];

    return (
        <div className="dyd-page">
            <Container>
                <div className="dyd-page-head">
                    <div>
                        <span className="dyd-eyebrow">Eventos presenciales</span>
                        <h1 style={{ marginTop: 16, marginBottom: 10 }}>
                            Nos vemos <span className="dyd-gradient-text">en persona</span>
                        </h1>
                    </div>
                    <button className="dyd-btn dyd-btn-primary" onClick={() => setShowEventModal(true)}>
                        <Plus size={17} strokeWidth={2.4} />
                        Crear evento
                    </button>
                </div>

                <p className="dyd-lead" style={{ marginBottom: 22 }}>
                    Explora los próximos eventos organizados por la comunidad. Están agrupados por mes para que no te pierdas ninguno.
                </p>

                <div className="dyd-note" style={{ marginBottom: 32 }}>
                    <Info size={18} />
                    <span>
                        <strong>Recordatorio:</strong> cuando crees un evento, compártelo también en el grupo
                        de WhatsApp del club para que todos puedan verlo a tiempo.
                    </span>
                </div>

                <div className="dyd-tabs" style={{ marginBottom: 20 }}>
                    <button
                        onClick={() => setTimeframe("proximos")}
                        className={`dyd-tab ${timeframe === "proximos" ? "active" : ""}`}
                    >
                        <CalendarRange size={15} /> Próximos
                        <span className="dyd-tab-count">{counts.proximos}</span>
                    </button>
                    <button
                        onClick={() => setTimeframe("anteriores")}
                        className={`dyd-tab ${timeframe === "anteriores" ? "active" : ""}`}
                    >
                        <History size={15} /> Anteriores
                        <span className="dyd-tab-count">{counts.anteriores}</span>
                    </button>
                </div>

                <div className="dyd-panel dyd-filters">
                    <div className="dyd-field" style={{ flex: '1 1 260px', maxWidth: 340 }}>
                        <label className="dyd-label" htmlFor="eventCategory">
                            <SlidersHorizontal size={12} style={{ marginRight: 6, verticalAlign: '-1px' }} />
                            Categoría
                        </label>
                        <select
                            id="eventCategory"
                            className="dyd-select"
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                        >
                            <option value="Todas">Todas las categorías</option>
                            {categorias.map((cat, index) => (
                                <option key={index} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {loading ? (
                    <Loader text="Cargando eventos" />
                ) : Object.keys(groupedEvents).length === 0 ? (
                    <EmptyState
                        icon={timeframe === "anteriores" ? History : CalendarOff}
                        title={timeframe === "anteriores"
                            ? "Todavía no hay eventos pasados en esta categoría"
                            : "No hay eventos próximos en esta categoría"}
                        hint={timeframe === "anteriores"
                            ? "Aquí quedará el histórico del club conforme se celebren."
                            : "Organiza el tuyo y reúne al club."}
                    />
                ) : (
                    Object.entries(groupedEvents).map(([monthYear, eventsInMonth]) => (
                        <section key={monthYear} style={{ marginBottom: 54 }}>
                            <div className="dyd-section-title">
                                <h2 style={{ margin: 0, textTransform: 'capitalize' }}>{monthYear}</h2>
                                <span className="dyd-count">{eventsInMonth.length}</span>
                            </div>

                            <div className="dyd-grid">
                                {eventsInMonth.map((event, index) => (
                                    <article
                                        key={event._id}
                                        id={`event-${event._id}`}
                                        className="dyd-card dyd-rise"
                                        style={{ '--i': index % 12 }}
                                        onMouseMove={trackPointer}
                                    >
                                        <div className="dyd-card-media">
                                            {hasCustomImage(event.eventImage) ? (
                                                <img src={event.eventImage} alt={event.name} />
                                            ) : (
                                                <MediaPlaceholder name={event.name} variant="card" />
                                            )}
                                            {isMyEvent(event) && (
                                                <span className="dyd-owner-badge" title="Este es tu evento">
                                                    <BadgeCheck size={13} /> Tuyo
                                                </span>
                                            )}
                                        </div>

                                        <div className="dyd-card-body">
                                            {event.category && <span className="dyd-chip">{event.category}</span>}
                                            <h3 className="dyd-card-title">{event.name}</h3>

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                <span className="dyd-meta">
                                                    <Calendar size={14} /> {formatDate(event.date)}
                                                </span>
                                                {event.time && (
                                                    <span className="dyd-meta"><Clock size={14} /> {event.time}</span>
                                                )}
                                                <span className="dyd-meta">
                                                    <MapPin size={14} /> {event.location || "Ubicación no definida"}
                                                </span>
                                            </div>

                                            <p className="dyd-card-text">
                                                {event.description && event.description.length > 120 ? (
                                                    <>
                                                        {event.description.substring(0, 120)}…{' '}
                                                        <button className="dyd-link-btn" onClick={() => handleVerMas(event)}>
                                                            Ver más
                                                        </button>
                                                    </>
                                                ) : (
                                                    event.description || "Sin descripción"
                                                )}
                                            </p>

                                            <div className="dyd-card-actions">
                                                {timeframe === "proximos" && (
                                                    <>
                                                        {isApuntado(event) ? (
                                                            <button
                                                                className="dyd-btn dyd-btn-danger dyd-btn-block"
                                                                onClick={() => {
                                                                    handleCloseModal();
                                                                    handleDesapuntarme(event);
                                                                }}
                                                            >
                                                                <UserMinus size={16} />
                                                                Desapuntarme
                                                            </button>
                                                        ) : (
                                                            <button
                                                                className="dyd-btn dyd-btn-success dyd-btn-block"
                                                                onClick={() => {
                                                                    handleCloseModal();
                                                                    handleApuntarme(event);
                                                                }}
                                                            >
                                                                <Hand size={16} />
                                                                Apuntarme
                                                            </button>
                                                        )}

                                                        <button
                                                            className="dyd-btn dyd-btn-ghost dyd-btn-block"
                                                            onClick={() => downloadICS(calendarData(event))}
                                                            title="Descarga un .ics que abre tu calendario"
                                                        >
                                                            <CalendarPlus size={16} />
                                                            Añadir al calendario
                                                        </button>
                                                    </>
                                                )}

                                                <ShareMenu
                                                    title={event.name}
                                                    lines={shareLines(event)}
                                                    url={eventUrl(event)}
                                                />

                                                {isMyEvent(event) && (
                                                    <button
                                                        className="dyd-btn dyd-btn-danger dyd-btn-sm dyd-btn-block"
                                                        onClick={() => {
                                                            if (window.confirm('¿Estás seguro de que quieres eliminar este evento?')) {
                                                                handleDelete(event._id);
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 size={14} />
                                                        Eliminar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>
                    ))
                )}

                {/* Modal con los detalles completos del evento */}
                <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{selectedEvent?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {hasCustomImage(selectedEvent?.eventImage) && (
                            <div className="dyd-modal-image">
                                <img src={selectedEvent.eventImage} alt={selectedEvent.name} />
                            </div>
                        )}

                        <div className="dyd-detail-grid">
                            <div className="dyd-detail">
                                <span className="dyd-label"><Calendar size={12} /> Fecha</span>
                                <span>{formatDate(selectedEvent?.date)}</span>
                            </div>
                            {selectedEvent?.time && (
                                <div className="dyd-detail">
                                    <span className="dyd-label"><Clock size={12} /> Hora</span>
                                    <span>{selectedEvent.time}</span>
                                </div>
                            )}
                            <div className="dyd-detail">
                                <span className="dyd-label"><MapPin size={12} /> Ubicación</span>
                                <span>{selectedEvent?.location || "No definida"}</span>
                            </div>
                            <div className="dyd-detail">
                                <span className="dyd-label"><Tag size={12} /> Categoría</span>
                                <span>{selectedEvent?.category}</span>
                            </div>
                        </div>

                        <div className="dyd-detail" style={{ marginTop: 18 }}>
                            <span className="dyd-label"><FileText size={12} /> Descripción</span>
                            <p style={{ margin: '6px 0 0' }}>
                                {selectedEvent?.description || "Sin descripción disponible"}
                            </p>
                        </div>
                    </Modal.Body>
                    <Modal.Footer style={{ gap: 10, flexWrap: 'wrap' }}>
                        <Button variant="secondary" onClick={handleCloseModal}>Cerrar</Button>

                        <button
                            className="dyd-btn dyd-btn-ghost"
                            onClick={() => selectedEvent && downloadICS(calendarData(selectedEvent))}
                        >
                            <CalendarPlus size={16} />
                            Descargar .ics
                        </button>

                        {selectedEvent && googleCalendarUrl(calendarData(selectedEvent)) && (
                            <a
                                className="dyd-btn dyd-btn-ghost"
                                href={googleCalendarUrl(calendarData(selectedEvent))}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Calendar size={16} />
                                Google Calendar
                            </a>
                        )}

                        {selectedEvent && (
                            <ShareMenu
                                title={selectedEvent.name}
                                lines={shareLines(selectedEvent)}
                                url={eventUrl(selectedEvent)}
                                className="dyd-btn dyd-btn-ghost"
                            />
                        )}

                        {selectedEvent && !isPast(selectedEvent) && (
                            isApuntado(selectedEvent) ? (
                                <button
                                    className="dyd-btn dyd-btn-danger"
                                    onClick={() => {
                                        handleDesapuntarme(selectedEvent);
                                        handleCloseModal();
                                    }}
                                >
                                    <UserMinus size={16} />
                                    Desapuntarme
                                </button>
                            ) : (
                                <button
                                    className="dyd-btn dyd-btn-success"
                                    onClick={() => {
                                        handleApuntarme(selectedEvent);
                                        handleCloseModal();
                                    }}
                                >
                                    <Hand size={16} />
                                    Apuntarme
                                </button>
                            )
                        )}
                    </Modal.Footer>
                </Modal>

                <EventCard isOpen={showEventModal} onClose={() => setShowEventModal(false)} />

                {/* Confirmación de inscripción o de baja */}
                <Modal show={!!pending} onHide={cancelParticipacion} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {pending?.action === 'leave' ? 'Confirmar baja' : 'Confirmar inscripción'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center">
                        {pending?.action === 'leave' ? (
                            <UserMinus size={34} strokeWidth={1.4} style={{ color: 'var(--dyd-danger)', marginBottom: 12 }} />
                        ) : (
                            <UserCheck size={34} strokeWidth={1.4} style={{ color: 'var(--dyd-success)', marginBottom: 12 }} />
                        )}
                        <h5 style={{ marginBottom: 6 }}>
                            {pending?.action === 'leave'
                                ? '¿Quieres desapuntarte de este evento?'
                                : '¿Quieres apuntarte a este evento?'}
                        </h5>
                        {pending?.event?.name && <p style={{ margin: 0 }}>{pending.event.name}</p>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={cancelParticipacion}>Cancelar</Button>
                        <button
                            className={`dyd-btn ${pending?.action === 'leave' ? 'dyd-btn-danger' : 'dyd-btn-success'}`}
                            onClick={confirmParticipacion}
                        >
                            Confirmar
                        </button>
                    </Modal.Footer>
                </Modal>
            </Container>

            <style>{`
                .dyd-owner-badge {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 10px;
                    border-radius: 999px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    letter-spacing: 0.04em;
                    color: #fff;
                    background: rgba(10,14,20,0.72);
                    border: 1px solid rgba(59,130,246,0.4);
                    backdrop-filter: blur(6px);
                }
                .dyd-owner-badge svg { color: var(--dyd-blue-light); }

                .dyd-link-btn {
                    padding: 0;
                    border: none;
                    background: none;
                    font: inherit;
                    font-weight: 600;
                    color: var(--dyd-blue-light);
                    cursor: pointer;
                }
                .dyd-link-btn:hover { color: #93c5fd; text-decoration: underline; }

                .dyd-modal-image {
                    display: flex;
                    justify-content: center;
                    padding: 14px;
                    margin-bottom: 20px;
                    border-radius: var(--dyd-r-md);
                    background: rgba(255,255,255,0.03);
                    border: 1px solid var(--dyd-border);
                }
                .dyd-modal-image img { max-width: 100%; max-height: 250px; object-fit: contain; }

                .dyd-detail-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(min(100%, 160px), 1fr));
                    gap: 14px;
                }
                .dyd-detail {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    padding: 13px 15px;
                    border-radius: var(--dyd-r-sm);
                    background: rgba(255,255,255,0.03);
                    border: 1px solid var(--dyd-border);
                    color: var(--dyd-text);
                    font-size: 0.92rem;
                }
                .dyd-detail .dyd-label { display: inline-flex; align-items: center; gap: 6px; }
                .dyd-detail .dyd-label svg { color: var(--dyd-blue-light); }
            `}</style>
        </div>
    );
}

export default EventsComponent;
